import { digitsOnly } from "@atos6/utils"
import { useContainer } from "./Container"
import { getOrganization } from "../gql/getOrganization"
import { ENV } from "../ENV"
import { updateActiveContribution } from "../gql/updateActiveContribution"
import { createAccount } from "../gql/createAccount"
import { openNotificationWithIcon } from "../components/Notification"
import { updateDocumentStatus } from "../gql/updateDocumentStatus"

export function useStore() {
  const [state, setState] = useContainer()
  // @ts-ignore FIXME: Como tipar o store?
  const store = {
    ...state,
    setState,

    get isReady() {
      return !!state.org
    },

    get isAccountCreated() {
      return !!state.contrib?.sellerId
    },

    get isFormAccountEmpty() {
      return (
        state.documentValue === "" ||
        state.name === "" ||
        state.bankId === "" ||
        state.agency === "" ||
        state.accountNumber === "" ||
        state.accountDigit === ""
      )
    },

    get isSaveButtonDisabled(): boolean {
      return store.isFormAccountEmpty || store.isAccountCreated
    },

    get zoopAuth() {
      return "Basic " + btoa(`${ENV.ZOOP_ZPK}:`)
    },

    get uploadUrl(): string {
      return `https://api.zoop.ws/v1/marketplaces/${ENV.ZOOP_MARKETPLACE}/sellers/${state.contrib?.sellerId}/documents`
    },

    get isCpf(): boolean {
      return state.documentType === "cpf"
    },

    get isDocumentOk(): boolean {
      return state.contrib?.statusGateway === "enabled"
    },

    get publicOnlineDonationUrl() {
      return state.contrib?.publicOnlineDonationUrl || ""
    },

    get gatewayPlans() {
      return state.contrib?.gatewayPlans?.collection || []
    },

    get currentPlan(): string {
      return store.gatewayPlans.find((plan: GatewayPlan) => plan.active)?.name || ""
    },

    get uploadView(): UploadView {
      if (store.isDocumentOk) return "DONE"
      if (store.isAccountCreated) return "WAITING_UPLOAD"
      return "WAITING_ACCOUNT"
    },

    async updateActive(active: boolean) {
      setState({ isLoading: true })
      try {
        const response = await updateActiveContribution(state.id, active)
        const contrib =
          response.updateOnlineContributionConfiguration.onlineContributionConfiguration
        store.setContribution(contrib)
      } finally {
        setState({ isLoading: false })
      }
    },

    // FIXME: Melhorar lógica de controle de loading
    setActive(active: boolean) {
      if (store.isAccountCreated) {
        store.updateActive(active)
      } else {
        setState({ active })
      }
    },

    setContribution(contrib: Partial<OnlineContributionConfiguration>) {
      let bankAccountType = contrib.bankAccount?.bankAccountType.split("_")[0] as BankAccountType
      if (bankAccountType !== "checking") bankAccountType = "savings"
      const accountNumber = digitsOnly(contrib.bankAccount?.number)
      const accountDigit = accountNumber.substr(accountNumber.length - 1)
      const payload = {
        name: contrib.name,
        documentType: contrib.documentType,
        documentValue: contrib.documentNumber,
        bankId: contrib.bankAccount?.bank.id,
        agency: contrib.bankAccount?.agency,
        bankAccountType,
        accountNumber,
        accountDigit,
        active: contrib.active,
        contrib,
      }
      setState(payload)
    },

    setOrganization(data: GetOrganizationResponse) {
      const banks =
        data.banks?.collection.map(bank => [bank.id, bank.code, bank.bankName] as const) || []
      let payload = { org: data.organization, banks }
      setState(payload)
      if (data.organization?.onlineContributionConfiguration) {
        store.setContribution(data.organization.onlineContributionConfiguration)
      }
    },

    async setDocumentOk() {
      setState({ isLoading: true })
      try {
        const response = await updateDocumentStatus(state.id, "enabled")
        const contrib =
          response.updateOnlineContributionConfiguration.onlineContributionConfiguration
        store.setContribution(contrib)
      } finally {
        setState({ isLoading: false })
      }
    },

    async createAccount() {
      // setState({ sellerId: "0cdd51c147de4e2eb7ca02ad300c5b4a" })
      const account: ContribAccount = {
        organizationId: state.id,
        bankAccountAgency: +state.agency,
        bankAccountDigit: +state.accountDigit,
        bankAccountNumber: +state.accountNumber,
        bankAccountType: state.bankAccountType + "_account",
        name: state.name,
        bankId: +state.bankId,
        documentType: state.documentType,
        documentNumber: state.documentValue,
      }
      try {
        setState({ isLoading: true })
        const result = await createAccount(account)
        if (result) {
          if (result?.createOnlineContributionConfiguration?.errors.length > 0) {
            const errors = result.createOnlineContributionConfiguration.errors
            const msg = errors.find(e => e.attribute === "document_number")
            if (msg) {
              setState({ errors: { documentValue: msg.message } })
            }
          } else {
            setState({
              errors: { documentValue: "" },
              contrib: result.createOnlineContributionConfiguration.onlineContributionConfiguration,
            })
          }
        }
      } catch (e) {
        openNotificationWithIcon("error", "Erro na operação", "Erro inesperado!")
      } finally {
        setState({ isLoading: false })
      }
    },

    // FIXME: Melhorar lógica de controle de loading
    async loadOrg() {
      if (state.id) {
        if (!state.org) {
          const data = await getOrganization(state.id)
          store.setOrganization(data)
        }
      } else {
        throw Error("Missing id")
      }
    },
  }

  return store
}
